import { useCallback } from 'react';
import { useCurrentUser } from '@qualio/ui-components';
import { DateTime } from 'luxon';

export enum DateTimeFormat {
  TASK = 'dd LLL yyyy hh:mm',
  APPROVER = "MMM dd, yyyy 'at' hh:mm a",
  STANDARD_DATE = 'd LLL yyyy',
  STANDARD_DATETIME = "d LLL yyyy 'at' h:mm a",
}

export const calcTaskDueDate = (createdAt: number, timeLimit: number, userTz: string): Date => {
  const dueDayNoon = DateTime.fromSeconds(createdAt, { zone: 'utc' })
    .startOf('day')
    .plus({ days: timeLimit })
    .set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
  // Re-encode noon as if it were in the user's timezone so localiseDate
  // (utcToZonedTime) adds the offset back and lands on the correct UTC day.
  return dueDayNoon.setZone(userTz, { keepLocalTime: true }).toUTC().toJSDate();
};

export const getLocalisedDate = (dateTime: DateTime, localiseDate: (utcDate: string | Date) => Date) => {
  const localisedDate = DateTime.fromJSDate(localiseDate(dateTime.toJSDate()));
  return localisedDate.toFormat(DateTimeFormat.STANDARD_DATE);
};

export const getDueValue = (
  referenceDatetime: DateTime,
  dueAtDatetime: DateTime,
  localiseDate: (utcDate: string | Date) => Date,
  isInFinalStatuses?: boolean,
): string => {
  const localisedDueAt = DateTime.fromJSDate(localiseDate(dueAtDatetime.toJSDate()));
  const localisedReferenceDatetime = DateTime.fromJSDate(localiseDate(referenceDatetime.toJSDate()));
  if (isInFinalStatuses) {
    return localisedDueAt.toFormat(DateTimeFormat.STANDARD_DATE);
  }

  const diffDays = localisedReferenceDatetime.diff(localisedDueAt, 'days').days;

  switch (true) {
    case diffDays === 0:
      return 'Today';
    case diffDays > 0:
      return localisedDueAt.toRelative({ base: localisedReferenceDatetime }) ?? '';
    case diffDays < 0:
      return localisedDueAt.toFormat(DateTimeFormat.STANDARD_DATE);
    default:
      return localisedDueAt.toFormat(DateTimeFormat.STANDARD_DATETIME);
  }
};

export const useLocalisedFormatDateTime = (): ((timestamp: number | string, includeTime?: boolean) => string) => {
  const { formatDate } = useCurrentUser();
  return useCallback(
    (timestamp: number | string, includeTime = true) => {
      const dateObj = Number.isFinite(timestamp)
        ? DateTime.fromSeconds(timestamp as number).toJSDate()
        : DateTime.fromISO(timestamp as string).toJSDate();
      return formatDate(dateObj, includeTime);
    },
    [formatDate],
  );
};

export const convertEpochToJsDate = (value: number) => {
  return DateTime.fromSeconds(value).toLocal().toJSDate();
};

export const convertUtcStringToDate = (value: string, fmt = "yyyy-MM-dd'T'HH:mm:ss") => {
  if (value) {
    return DateTime.fromFormat(value, fmt, { zone: 'UTC' }).toJSDate();
  }
  return value;
};

export const convertStringToDateTime = (value: string, fmt = "yyyy-MM-dd'T'HH:mm:ssZ") => {
  return DateTime.fromFormat(value, fmt).toLocal();
};

export const convertISOCalendarDateToDisplayDate = (value: string) => {
  // This is only used for displaying date values that are saved as date string
  // so no localization is needed for this
  return DateTime.fromISO(value).toFormat(DateTimeFormat.STANDARD_DATE);
};

// Returns the UTC instant for the start of the picked calendar day, interpreted
// in `zone` (the viewer's timezone). A user picks a calendar day meaning their
// local day, so the boundary must be resolved in their zone before converting to
// UTC. Defaults to 'utc' for callers that have not yet been localised.
export const getUtcStartOfDay = (dateString: string | undefined, zone = 'utc'): string | undefined => {
  if (!dateString) {
    return undefined;
  }
  const dt = DateTime.fromISO(dateString, { zone }).startOf('day').toUTC();
  return dt.isValid ? dt.toISO() : undefined;
};

export const getUtcEndOfDay = (dateString: string | undefined, zone = 'utc'): string | undefined => {
  if (!dateString) {
    return undefined;
  }
  const dt = DateTime.fromISO(dateString, { zone }).endOf('day').toUTC();
  return dt.isValid ? dt.toISO() : undefined;
};

export const convertDateToString = (value: string | Date) => {
  if (value === null || typeof value === 'string') {
    return value;
  }
  return DateTime.fromJSDate(value).toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss");
};

export const convertDateToISOString = (value?: Date) => {
  if (value) {
    return DateTime.fromJSDate(value).toFormat("yyyy-MM-dd'T'HH:mm:ss");
  }
};

export const formatDatePickerComponentDateToString = (d: Date) =>
  DateTime.fromJSDate(d, { zone: 'utc' }).toFormat('yyyy-MM-dd');
